import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormInstance } from 'rsuite';
import {
  getFormValidationStatusAsync,
  scrollToFormErr,
} from 'utils/form.util';
import { FirebaseError } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  linkWithCredential,
  OAuthCredential,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import useUserStore from 'store/useUserStore';
import serverErrorHandler from 'services/serverError.service';
import { openErrorToaster } from 'services/toast.service';
import { Link } from 'react-router-dom';
import useUrlQuery from 'hooks/useUrlQuery';
import { setPostLoginRedirect } from 'utils/postLoginRedirect';
import { requestAuthSessionSync } from 'utils/authSessionSync';
import { getSclaAppHomePath } from 'utils/navigation';
import signUp from '../api/signUp';
import signUpWithGoogle from '../api/signUpWithGoogle';
import { authSignUpFormModel } from '../validation/schemas';
import getSclaSignUpInvite from '../api/getSclaSignUpInvite';
import {
  hasSignUpPrefill,
  readSignUpPrefill,
  type SignUpPrefill,
} from '../utils/signUpPrefill';

export interface ISignUpForm {
  firstName: string;
  lastName: string;
  school: string;
  schoolOther: string;
  grade: string;
  phoneNumber: string;
  email: string;
  ethnicity: string;
  ethnicityOther: string;
  password: string;
  verifyPassword: string;
}

const INIT_FORM: ISignUpForm = {
  firstName: '',
  lastName: '',
  school: '',
  schoolOther: '',
  grade: '',
  phoneNumber: '',
  email: '',
  ethnicity: '',
  ethnicityOther: '',
  password: '',
  verifyPassword: '',
};

type FormError = Partial<Record<keyof ISignUpForm, React.ReactNode>>;

function rememberApplicationDestination() {
  setPostLoginRedirect(getSclaAppHomePath());
}

export default function useSignup() {
  const query = useUrlQuery();
  // SCLA interest emails link here with ?invite=<token>; the applicant's
  // details are fetched from the API instead of being in the URL.
  const inviteToken = query.get('invite') || '';
  const [prefill, setPrefill] = useState<SignUpPrefill>({});
  const [formValue, setFormValue] = useState<ISignUpForm>({ ...INIT_FORM });
  const [formError, setFormError] = useState<FormError>({});
  const [isLoading, setIsLoading] = useState(false);
  const [pendingGoogleCredential, setPendingGoogleCredential] =
    useState<OAuthCredential | null>(null);
  const [pendingGoogleEmail, setPendingGoogleEmail] = useState<string | null>(
    null,
  );
  const formRef = useRef<FormInstance>(null);

  const storedEmail = useUserStore(s => s.storedEmail);

  useEffect(() => {
    if (!inviteToken) return undefined;

    let mounted = true;
    getSclaSignUpInvite(inviteToken)
      .then(invite => {
        if (!mounted || !invite) return;
        const next = readSignUpPrefill(
          new URLSearchParams({
            firstName: invite.firstName || '',
            lastName: invite.lastName || '',
            email: invite.email || '',
            phoneNumber: invite.phoneNumber || '',
            schoolName: invite.schoolName || '',
            gradeLevel: invite.gradeLevel || '',
          }),
        );
        setPrefill(next);
        // Fill only fields the applicant has not typed in yet.
        setFormValue(current => {
          const merged = { ...current };
          (Object.keys(next) as Array<keyof SignUpPrefill>).forEach(key => {
            const value = next[key];
            const field = key as keyof ISignUpForm;
            if (value && !merged[field]) {
              (merged as Record<string, unknown>)[field] = value;
            }
          });
          return merged;
        });
      })
      .catch(() => undefined);

    return () => {
      mounted = false;
    };
  }, [inviteToken]);

  useEffect(() => {
    let mounted = true;

    if (mounted && storedEmail) {
      setFormValue(current => (
        current.email ? current : { ...current, email: storedEmail }
      ));
    }

    return () => {
      mounted = false;
    };
  }, [storedEmail]);

  const mapServerErrorToFields = (error: any): FormError => {
    const status = error?.response?.status;
    const message: string =
      error?.response?.data?.message ?? error?.message ?? '';

    if (status === 409) {
      return {
        email: React.createElement(
          React.Fragment,
          null,
          'An account with this email already exists. ',
          React.createElement(Link, { to: '/' }, 'Sign in'),
          '.',
        ),
      };
    }

    if (status === 400) {
      const next: FormError = {};
      if (/phone/i.test(message)) next.phoneNumber = message;
      else if (/password|8 characters/i.test(message)) next.password = message;
      else if (/email/i.test(message)) next.email = message;
      else if (/first name/i.test(message)) next.firstName = message;
      else if (/last name/i.test(message)) next.lastName = message;
      else next.email = message;
      return next;
    }

    return {};
  };

  const onSubmit = async () => {
    try {
      const checkResult = authSignUpFormModel.check(formValue);
      const isValid = await getFormValidationStatusAsync(checkResult);
      if (!isValid) {
        scrollToFormErr();
        formRef.current?.check?.();
        return;
      }

      setIsLoading(true);
      const schoolName =
        formValue.school.toLowerCase() === 'other'
          ? (formValue.schoolOther || '').trim()
          : formValue.school;
      const signUpData = {
        firstName: formValue.firstName,
        lastName: formValue.lastName,
        email: formValue.email,
        phoneNumber: formValue.phoneNumber,
        password: formValue.password,
        school: schoolName,
        grade: formValue.grade,
        ethnicity: formValue.ethnicity || undefined,
        ethnicityOther:
          formValue.ethnicity === 'other'
            ? (formValue.ethnicityOther || '').trim()
            : undefined,
      };
      await signUp(signUpData);
      rememberApplicationDestination();

      const auth = getAuth();
      await setPersistence(auth, browserLocalPersistence);
      // The backend sends the verification email through SES.
      await signInWithEmailAndPassword(
        auth,
        formValue.email,
        formValue.password,
      );
    } catch (e: any) {
      setIsLoading(false);
      if (e?.response?.status === 429) {
        openErrorToaster(
          'Too many attempts. Try again in a few minutes.',
          4000,
        );
        return;
      }
      const fieldErrors = mapServerErrorToFields(e);
      if (Object.keys(fieldErrors).length > 0) {
        setFormError(fieldErrors);
        return;
      }
      serverErrorHandler(e);
    }
  };

  const onGoogleSignUp = async () => {
    const auth = getAuth();
    try {
      setIsLoading(true);
      rememberApplicationDestination();
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      const googleCredential = GoogleAuthProvider.credentialFromResult(result);
      const googleEmail = result.user.email;

      try {
        // Idempotent with useAuth auto-provision (200 if already created).
        await signUpWithGoogle();
        if (result.user) requestAuthSessionSync(result.user);
      } catch (backendError: any) {
        setIsLoading(false);
        if (backendError?.response?.status === 409) {
          await signOut(auth);
          setPendingGoogleCredential(googleCredential);
          setPendingGoogleEmail(googleEmail);
        } else {
          serverErrorHandler(backendError);
        }
      }
    } catch (e: unknown) {
      setIsLoading(false);
      if (
        e instanceof FirebaseError &&
        e.code === 'auth/account-exists-with-different-credential'
      ) {
        const credential = GoogleAuthProvider.credentialFromError(e);
        const email = (e.customData?.email as string) ?? null;
        setPendingGoogleCredential(credential);
        setPendingGoogleEmail(email);
      } else {
        serverErrorHandler(e);
      }
    }
  };

  const completeLinking = async (password: string) => {
    if (!pendingGoogleCredential || !pendingGoogleEmail) return;
    try {
      setIsLoading(true);
      const auth = getAuth();
      const result = await signInWithEmailAndPassword(
        auth,
        pendingGoogleEmail,
        password,
      );
      await linkWithCredential(result.user, pendingGoogleCredential);
      setPendingGoogleCredential(null);
      setPendingGoogleEmail(null);
      requestAuthSessionSync(result.user);
    } catch (e) {
      setIsLoading(false);
      serverErrorHandler(e);
    }
  };

  const cancelLinking = async () => {
    setPendingGoogleCredential(null);
    setPendingGoogleEmail(null);
    try {
      await signOut(getAuth());
    } catch {
      // ignore
    }
  };

  const reconcilePrefillSchool = useCallback(
    (knownSchools: Set<string>, options: Array<{ value: string }>) => {
      setFormValue(current => {
        if (!current.school || current.school === 'other') return current;
        const match = options.find(
          option => option.value.toLocaleLowerCase() === current.school.toLocaleLowerCase(),
        );
        if (match) {
          if (match.value === current.school) return current;
          return { ...current, school: match.value };
        }
        if (knownSchools.size === 0) return current;
        return {
          ...current,
          school: 'other',
          schoolOther: current.schoolOther || current.school,
        };
      });
    },
    [],
  );

  const onChange = (val: ISignUpForm) => {
    setFormValue(val);
    if (Object.keys(formError).length > 0) {
      setFormError({});
    }
  };

  return {
    formValue,
    formError,
    isLoading,
    model: authSignUpFormModel,
    onChange,
    onSubmit,
    onGoogleSignUp,
    pendingGoogleEmail,
    completeLinking,
    cancelLinking,
    formRef,
    hasPrefill: hasSignUpPrefill(prefill),
    reconcilePrefillSchool,
  };
}
